/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useRef } from 'react';
import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useRiskClassSelectContext } from '@/context/riskClassSelectContext';
import Fuse from 'fuse.js';
import { FULL_NEEDS_ANALYSIS } from '../forms/CreateNewBusinessForm/validationSchema';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const FUSE_OPTIONS = {
	keys: ['classOfRisk', 'label'],
	threshold: 0.2,
};

const FULL_NEEDS_ANALYSIS_DOC = {
	isParent: false,
	label: 'Full needs analysis',
	classOfRisk: FULL_NEEDS_ANALYSIS,
};

const RiskClassAutocomplete = ({ disabled, clientType, ...props }) => {
	const { selectedItems, setSelectedItems } = useRiskClassSelectContext();

	const containerRef = useRef();

	const classesOfRiskKeys = selectedItems.map((i) => i.classOfRisk);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery({ email }, { skip: !email });

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType, brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized },
	);

	const parents = (classesOfRisk ?? []).filter((i) => i.isParent);
	const children = (classesOfRisk ?? []).filter((i) => !i.isParent);

	const { getFeatureFlags } = useFeatureFlagContext();
	const showFullNeedsAnalysis = Boolean(
		getFeatureFlags('showFullNeedsAnalysis')?.active,
	);

	const fullNeedsDoc = useMemo(
		() =>
			showFullNeedsAnalysis
				? {
						...FULL_NEEDS_ANALYSIS_DOC,
						clientTypes: [clientType],
						parent: clientType,
						countryCode,
					}
				: null,
		[showFullNeedsAnalysis, clientType, countryCode],
	);

	const sortedClassesOfRisk = useMemo(() => {
		const data = children.slice();
		const classOfRiskKeys = data.reduce((acc, v) => {
			if (!acc[v.classOfRisk]) {
				acc[v.classOfRisk] = v;
			}
			return acc;
		}, {});

		const uniqueClasses = Object.values(classOfRiskKeys);
		// Sort by the 'order' field from Firebase (default to 999 if no order)
		const sorted = [...uniqueClasses].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

		return [...[fullNeedsDoc].filter(Boolean), ...sorted];
	}, [children, fullNeedsDoc]);

	const isItemDisabled = (item) => {
		const conditions = item.conditions ?? {};
		const andConditions = conditions.and ?? [];
		const orConditions = conditions.or ?? [];

		const hasAllAndConditions =
			andConditions.length === 0 || andConditions.every((c) => classesOfRiskKeys.includes(c));

		const hasAnyOrCondition =
			orConditions.length === 0 || orConditions.some((c) => classesOfRiskKeys.includes(c));

		return disabled || item.isParent || !hasAllAndConditions || !hasAnyOrCondition;
	};

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, sortedClassesOfRisk);
		return new Fuse(sortedClassesOfRisk, FUSE_OPTIONS, fuseIndex);
	}, [sortedClassesOfRisk]);

	const fullNeedsChecked = children.every((k) => classesOfRiskKeys.includes(k.classOfRisk));

	return (
		<Autocomplete
			ref={containerRef}
			fullWidth
			disabled={
				disabled || !classesOfRiskQuery.isSuccess || !userQuery.isSuccess || !clientType
			}
			isOptionEqualToValue={(option, val) =>
				option?.id === val?.id || option?.classOfRisk === val?.classOfRisk
			}
			getOptionLabel={(option) => option?.label ?? ''}
			options={sortedClassesOfRisk}
			loading={!classesOfRiskQuery.isSuccess || !userQuery.isSuccess}
			value={selectedItems ?? ''}
			multiple
			filterOptions={(options, params) => {
				const search = params.inputValue;
				if (options.length > 0 && search) {
					return searchFuse.search(search).flatMap((v) => v?.item);
				}
				return options;
			}}
			onChange={(_, v) => {
				if (v.some((r) => r.classOfRisk === FULL_NEEDS_ANALYSIS)) {
					const sortedChildren = children
						.slice()
						.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
					console.log('Full needs checked?', {
						fullNeedsChecked,
						newChildren: fullNeedsChecked ? [] : sortedChildren,
					});
					setSelectedItems(fullNeedsChecked ? [] : sortedChildren);
				} else {
					setSelectedItems(v.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
				}
			}}
			getOptionDisabled={(option) => {
				return isItemDisabled(option);
			}}
			renderOption={(props, option) => {
				const checked =
					classesOfRiskKeys.includes(option.classOfRisk) ||
					(option.classOfRisk === FULL_NEEDS_ANALYSIS && fullNeedsChecked);
				const Icon = checked ? CheckBox : CheckBoxOutlineBlank;
				const { key, ...p } = props;
				return (
					<li key={key} {...p} style={{ width: '100%' }}>
						<Stack direction='row' width='100%' spacing={1}>
							<Icon {...(checked && { color: 'primary' })} fontSize='small' />
							<Typography
								{...(option.classOfRisk === FULL_NEEDS_ANALYSIS && {
									fontWeight: 'bold',
								})}
							>
								{option.label ?? ''}
							</Typography>
						</Stack>
					</li>
				);
			}}
			disableCloseOnSelect
			renderValue={() => <></>}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						slotProps={{
							input: {
								...params.InputProps,
								endAdornment: (
									<React.Fragment>
										{classesOfRiskQuery.isLoading ? (
											<CircularProgress color='inherit' size={20} />
										) : null}
										{params.InputProps.endAdornment}
									</React.Fragment>
								),
							},
						}}
					/>
				);
			}}
			{...(props ?? {})}
			sx={{
				...(props?.sx ?? {}),
			}}
		/>
	);
};

export default RiskClassAutocomplete;
